import { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material';
import { useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import { graphql } from '../../../../../../gql';
import { Quote } from '../../../../../../Typescript/backend/classes';
import { useUsingVagoSettings } from '../../../../../../hooks';
import { useAuth } from '@calefy-inc/authentication';
import {
  GET_TASK_POOL,
  GET_ALL_QUOTE_EDITIONS,
} from '../../../../../../queries';
import Bugsnag from '@bugsnag/js';
import { errorify } from '@calefy-inc/utility';
import { CoreTaskStatusChoices } from '../../../../../../gql/graphql';
import { TaskPoolProgress } from '../../../../../common/TaskPoolProgress';
import { DisplayInlineError } from '../../../../../common/ErrorHandling';
import { useNextQuoteEdition } from '../../../../../../hooks';
import { GET_QUOTE_EDITION_RELATIONSHIPS } from '../../../../../../hooks/useQuoteEditions';

// ----------------------------------------------------------------------

const POLLING_INTERVAL_MS = 500;

// ----------------------------------------------------------------------

interface VagoInitiateRenewalButtonProps {
  quote: Quote | null;
}

export const VagoInitiateRenewalButton = ({
  quote,
}: VagoInitiateRenewalButtonProps) => {
  const onVago = useUsingVagoSettings();
  const relationship = useNextQuoteEdition(quote?.uniqueId);
  const [open, setOpen] = useState<boolean>(false);

  if (!onVago) {
    return null;
  }

  if (!quote) {
    return null;
  }

  return (
    <Tooltip title={relationship ? 'Quotes can only be renewed once.' : ''}>
      <span>
        <Button disabled={!!relationship} onClick={() => setOpen(true)}>
          {relationship ? 'Already Renewed' : 'Renew Application'}
        </Button>
        <VagoInitiateRenewalDialog
          quote={quote}
          open={open}
          setOpen={(open) => setOpen(open)}
        />
      </span>
    </Tooltip>
  );
};

interface VagoInitiateRenewalDialogProps {
  quote: Quote;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const VagoInitiateRenewalDialog = ({
  quote,
  open,
  setOpen,
}: VagoInitiateRenewalDialogProps) => {
  const { token } = useAuth();
  const client = useApolloClient();
  const [copying, setCopying] = useState<boolean>(false);
  const [newQuoteUuid, setNewQuoteUuid] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<Error | null>(null);
  const [indexPercent, setIndexPercent] = useState<string>('0');
  const [validationError, setValidationError] = useState<Error | null>(null);

  const [
    vagoInitiateQuoteCopy,
    {
      error: copyMutationError,
      data: copyMutationData,
      loading: copyMutationLoading,
      called: copyMutationCalled,
    },
  ] = useMutation(VAGO_INITIATE_QUOTE_COPY);

  const [
    getTaskPool,
    {
      error: taskPoolError,
      data: taskPoolData,
      loading: taskPoolLoading,
      called: taskPoolCalled,
      stopPolling,
    },
  ] = useLazyQuery(GET_TASK_POOL, {
    pollInterval: POLLING_INTERVAL_MS,
  });

  // Start polling when we get a task pool ID from the mutation
  useEffect(() => {
    if (copyMutationData?.vagoInitiateQuoteCopy?.taskPool?.id) {
      setCopying(true);
      setCopyError(null);
      getTaskPool({
        variables: {
          taskPoolId: copyMutationData.vagoInitiateQuoteCopy.taskPool.id,
        },
      });
    }
  }, [copyMutationData, getTaskPool]);

  // Set copying to false if there's an error or if there is more than one task, all of which are complete
  useEffect(() => {
    if (
      copyMutationError ||
      taskPoolError ||
      taskPoolData?.getTaskPool?.error
    ) {
      setCopying(false);
      setCopyError(
        copyMutationError ||
          taskPoolError ||
          new Error(taskPoolData?.getTaskPool?.error || 'Unknown error'),
      );
      stopPolling?.();
    } else if (taskPoolData?.getTaskPool) {
      const tasks = taskPoolData.getTaskPool.tasks;
      const allTasksCompleted =
        tasks &&
        tasks.length > 0 &&
        tasks.every(
          (task) =>
            task.status === CoreTaskStatusChoices.Completed ||
            task.status === CoreTaskStatusChoices.Failed,
        );
      if (allTasksCompleted) {
        setCopying(false);
        stopPolling?.();
      }
    }
  }, [copyMutationError, taskPoolError, taskPoolData, stopPolling]);

  // Extract the new quote UUID from the task pool's additionalInformation only once copying is done
  useEffect(() => {
    if (!taskPoolLoading && !copying && taskPoolData?.getTaskPool) {
      try {
        const additionalInformationJson =
          taskPoolData?.getTaskPool?.additionalInformation;
        if (additionalInformationJson) {
          const parsed = JSON.parse(additionalInformationJson);
          const uuid = parsed.new_quote_unique_id;
          if (uuid) {
            setNewQuoteUuid(uuid);
          }
        }
      } catch (e) {
        console.error(e);
        Bugsnag.notify(errorify(e));
      }
    }
  }, [taskPoolLoading, copying, taskPoolData]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setCopying(false);
      setNewQuoteUuid(null);
      setCopyError(null);
      setIndexPercent('0');
      setValidationError(null);
      stopPolling?.();
    }
  }, [open, stopPolling]);

  // Validate index percent input
  const handleIndexPercentChange = (value: string) => {
    setIndexPercent(value);
    const trimmedValue = value.trim();
    const parsed = parseFloat(trimmedValue);
    if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
      setValidationError(new Error('Please enter a valid number'));
    } else {
      setValidationError(null);
    }
  };

  const handleCopy = () => {
    if (!token || !quote) {
      return;
    }
    // Clear any existing state
    setCopying(false);
    setNewQuoteUuid(null);
    setCopyError(null);
    setIndexPercent('0');
    setValidationError(null);
    stopPolling?.();
    const trimmedIndexPercent = indexPercent.trim();
    const parsedIndexPercent = parseFloat(trimmedIndexPercent);
    const finalIndexPercent =
      Number.isNaN(parsedIndexPercent) || !Number.isFinite(parsedIndexPercent)
        ? 0
        : parsedIndexPercent;
    vagoInitiateQuoteCopy({
      variables: {
        token,
        quoteUniqueId: quote.uniqueId,
        indexPercent: finalIndexPercent,
      },
    });
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth='md' fullWidth>
      <DialogTitle>Renewal</DialogTitle>
      <DialogContent sx={{ overflow: 'auto' }}>
        <IndexPercentInputContainer>
          <TextField
            label='Index Percent'
            type='number'
            value={indexPercent}
            onChange={(e) => handleIndexPercentChange(e.target.value)}
            fullWidth
            error={!!validationError}
            disabled={copyMutationCalled}
            InputProps={{
              endAdornment: <InputAdornment position='end'>%</InputAdornment>,
            }}
          />
          <Typography variant='caption' color='text.secondary' sx={{ mt: 1 }}>
            Any entered amount will be applied to the building and rental
            limits.
          </Typography>
          <DisplayInlineError error={validationError}>
            Invalid number
          </DisplayInlineError>
        </IndexPercentInputContainer>
        <Collapse in={copyMutationLoading}>
          <Typography>Getting things set up...</Typography>
        </Collapse>
        {taskPoolCalled ? (
          <TaskPoolProgress
            taskPool={taskPoolData?.getTaskPool}
            onPercentageChange={async (percentage) => {
              if (percentage === 100 && quote?.uniqueId && token) {
                // Refetch edition/relationship queries after renewal completes
                await client.refetchQueries({
                  include: [
                    GET_QUOTE_EDITION_RELATIONSHIPS,
                    GET_ALL_QUOTE_EDITIONS,
                  ],
                });
              }
            }}
          />
        ) : null}
        {newQuoteUuid ? (
          <RenewalCompleteContainer>
            <RenewalCompleteComponent quoteUuid={newQuoteUuid} />
          </RenewalCompleteContainer>
        ) : null}
        <DisplayInlineError
          error={copyError}
          processError={processRenewalError}
        >
          Error renewing application
        </DisplayInlineError>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCopy}
          variant='contained'
          disabled={
            copying ||
            !!newQuoteUuid ||
            !!copyError ||
            !!validationError ||
            copyMutationLoading ||
            taskPoolLoading
          }
        >
          Renew
        </Button>
        <Button
          onClick={() => setOpen(false)}
          color='inherit'
          disabled={copying || copyMutationLoading || taskPoolLoading}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface RenewalCompleteComponentProps {
  quoteUuid: string;
}

const RenewalCompleteComponent = ({
  quoteUuid,
}: RenewalCompleteComponentProps) => {
  return (
    <>
      <Typography align='center'>
        Renewal successfully completed! Click the button below to view the
        renewed application.
      </Typography>
      <Button
        variant='contained'
        href={`/insurtech/quote/resume/${quoteUuid}`}
        sx={{ mt: 2, display: 'block', mx: 'auto' }}
      >
        View Renewed Application
      </Button>
    </>
  );
};

const IndexPercentInputContainer = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(3),
}));

const RenewalCompleteContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  padding: theme.spacing(2, 0),
}));

const processRenewalError = (error: Error) => {
  if (error.message.match(/quote .+ already has a next edition/i)) {
    return 'Quote has already been renewed. Please see the details page for the up-to-date edition.';
  }
};

const VAGO_INITIATE_QUOTE_COPY = graphql(`
  mutation vagoInitiateQuoteCopy(
    $token: String!
    $quoteUniqueId: String!
    $indexPercent: Float!
  ) {
    vagoInitiateQuoteCopy(
      token: $token
      quoteUniqueId: $quoteUniqueId
      indexPercent: $indexPercent
    ) {
      taskPool {
        id
      }
    }
  }
`);
